// Shared by the worker and the admin `extractSegments` query so override keys
// cannot diverge. `segments[i]`/`locators[i]` pair up; emit order MUST match the
// reassembly walk (`mapTranslatableStrings` / `applyTextNodes`).
//
// A segment is one SENTENCE of a text block. Inline formatting (bold, italic,
// underline, link) inside the sentence is serialised as paired tags
// (`Refer to <t0>Ecotourism Australia</t0> to find`) so the whole sentence is
// translated as a unit and the model can move words across a formatting
// boundary. Reassembly parses the translated tagged sentence back into text
// nodes, recovering each tag's mark (including a link's href) and the block's
// colour from the English source. Colour is presentational: it is never a tag
// and never sent to the model.

import type { SegmentLocator } from './translationSegmentKey';
import { hashSourceText, toSegmentKey } from './translationSegmentKey';

// Tiptap mark stamped on segments the partial preview leaves in English. Shared
// so the DukeTiptapEditor's mark extension and the overlay agree on the name.
export const EDITED_SINCE_PUBLISH_MARK = 'editedSincePublish';

// Folded into the engine fingerprint, which cannot see code changes in this
// module. Bump when a change to extraction or reassembly alters what a rebuilt
// artifact would contain; leave it alone for refactors that preserve output.
// The golden spec (translationSegmentExtract.golden.spec.ts) fails when output
// changes without a bump, so forgetting is caught in CI.
export const SEGMENTER_VERSION = 1;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isTextNode(value: Record<string, unknown>): value is { type: 'text'; text: string } {
  return value['type'] === 'text' && typeof value['text'] === 'string';
}

function isConversation(content: unknown): content is { nodes: unknown[] } {
  return isRecord(content) && Array.isArray(content['nodes']);
}

// -- Marks ---------------------------------------------------------------------

const SEMANTIC_TYPES = ['bold', 'italic', 'underline', 'strike', 'code', 'link'] as const;
type SemMark = { type: string; attrs?: Record<string, unknown> };

function stableAttrs(attrs: Record<string, unknown>): string {
  return JSON.stringify(attrs, Object.keys(attrs).sort());
}

function markKey(mark: SemMark): string {
  return mark.attrs === undefined ? mark.type : `${mark.type}:${stableAttrs(mark.attrs)}`;
}

// Semantic marks in a fixed order so tag assignment is deterministic.
function semanticMarks(node: Record<string, unknown>): SemMark[] {
  const marks = node['marks'];
  if (!Array.isArray(marks)) {
    return [];
  }
  const found: SemMark[] = [];
  for (const type of SEMANTIC_TYPES) {
    for (const mark of marks) {
      if (isRecord(mark) && mark['type'] === type) {
        const attrs = mark['attrs'];
        found.push(isRecord(attrs) ? { type, attrs } : { type });
      }
    }
  }
  return found;
}

function colourOf(node: Record<string, unknown>): string | null {
  const marks = node['marks'];
  if (!Array.isArray(marks)) {
    return null;
  }
  for (const mark of marks) {
    if (isRecord(mark) && mark['type'] === 'textStyle' && isRecord(mark['attrs'])) {
      const colour = mark['attrs']['color'];
      if (typeof colour === 'string') {
        return colour;
      }
    }
  }
  return null;
}

// -- Sentence splitting --------------------------------------------------------

// Segment identity depends on these boundaries, and composition runs in both the
// worker (generation) and the browser (serving), so the two must split the same.
// Both use ICU sentence rules (UAX #29) via Intl.Segmenter, which agree on
// English boundaries. If an ICU version ever did differ, the mismatched segment
// simply serves English (its key stops matching the artifact) — a safe fallback,
// never a break. The abbreviation merge below is part of those boundaries, so
// changing its list moves segment keys for any document the list matches.

// Abbreviations that are effectively never the last word of a sentence. UAX #29
// carries no abbreviation dictionary, so it breaks after any of these as soon as a
// capitalised word follows: "meeting with Mr." and "Tran, an older client" become
// separate sentences. A fragment ending in a title cannot be translated on its own,
// so a translator reaches into the surrounding context and renders the whole
// sentence, producing output that overlaps the neighbouring segment.
//
// Only abbreviations that cannot end a sentence belong here. "Inc." and "etc." are
// left out on purpose: both legitimately finish a sentence, and merging there would
// join two real ones. Getting a merge wrong yields a coarser segment that still
// translates correctly; getting a split wrong cannot be recovered from.
const NEVER_SENTENCE_FINAL =
  /(?:^|[\s("'“‘])(?:Mr|Mrs|Ms|Mx|Dr|Prof|Rev|Hon|Messrs|vs|e\.g|i\.e)\.["')\]]*\s*$/;

// Reattaches a range that ends in such an abbreviation to the one after it. Runs on
// the merged text, so "Mr. and Mrs. Tran" collapses in one pass without chaining
// past the sentence that actually follows.
function mergeAbbreviationBreaks(text: string, ranges: [number, number][]): [number, number][] {
  const merged: [number, number][] = [];
  for (const [from, to] of ranges) {
    const previous = merged[merged.length - 1];
    if (previous !== undefined && NEVER_SENTENCE_FINAL.test(text.slice(previous[0], previous[1]))) {
      previous[1] = to;
      continue;
    }
    merged.push([from, to]);
  }
  return merged;
}

function sentenceRanges(text: string): [number, number][] {
  return mergeAbbreviationBreaks(text, rawSentenceRanges(text));
}

function rawSentenceRanges(text: string): [number, number][] {
  if (text === '') {
    return [];
  }
  if (typeof Intl.Segmenter === 'function') {
    const segmenter = new Intl.Segmenter('en', { granularity: 'sentence' });
    const ranges: [number, number][] = [];
    for (const part of segmenter.segment(text)) {
      ranges.push([part.index, part.index + part.segment.length]);
    }
    return ranges;
  }
  // Fallback for runtimes without Intl.Segmenter: break after sentence-final
  // punctuation followed by whitespace; otherwise the whole run is one sentence.
  const ranges: [number, number][] = [];
  let start = 0;
  const re = /[.!?]+["')\]]*\s+/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    const end = match.index + match[0].length;
    ranges.push([start, end]);
    start = end;
  }
  if (start < text.length) {
    ranges.push([start, text.length]);
  }
  return ranges;
}

// -- Runs, sentences, tag serialisation ---------------------------------------

type Piece = { text: string; sem: SemMark[]; colour: string | null };
type Sentence = {
  tagged: string;
  plain: string;
  tagMap: Map<string, SemMark>;
  colour: string | null;
  sourceNodes: Record<string, unknown>[];
};

function piecesOf(textNodes: Record<string, unknown>[]): Piece[] {
  return textNodes.map((node) => ({
    text: typeof node['text'] === 'string' ? node['text'] : '',
    sem: semanticMarks(node),
    colour: colourOf(node),
  }));
}

// Slice the concatenated run into the [from, to) window, splitting a piece when
// the boundary falls inside it, preserving each slice's marks.
function slicePieces(pieces: Piece[], from: number, to: number): Piece[] {
  const out: Piece[] = [];
  let cursor = 0;
  for (const piece of pieces) {
    const start = cursor;
    const end = cursor + piece.text.length;
    cursor = end;
    const lo = Math.max(from, start);
    const hi = Math.min(to, end);
    if (hi <= lo) {
      continue;
    }
    out.push({
      text: piece.text.slice(lo - start, hi - start),
      sem: piece.sem,
      colour: piece.colour,
    });
  }
  return out;
}

function escapeText(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function unescapeText(text: string): string {
  return text.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');
}

// Serialise a sentence's pieces to a tagged string, closing all open tags and
// reopening on any change to the active mark set (always well-formed nesting).
function serialiseSentence(pieces: Piece[]): { tagged: string; tagMap: Map<string, SemMark> } {
  const idByKey = new Map<string, string>();
  const tagMap = new Map<string, SemMark>();
  const idsFor = (marks: SemMark[]): string[] =>
    marks.map((mark) => {
      const key = markKey(mark);
      let id = idByKey.get(key);
      if (id === undefined) {
        id = `t${idByKey.size}`;
        idByKey.set(key, id);
        tagMap.set(id, mark);
      }
      return id;
    });

  const out: string[] = [];
  let open: string[] = [];
  for (const piece of pieces) {
    const ids = idsFor(piece.sem);
    if (ids.join(' ') !== open.join(' ')) {
      for (const id of [...open].reverse()) {
        out.push(`</${id}>`);
      }
      for (const id of ids) {
        out.push(`<${id}>`);
      }
      open = ids;
    }
    out.push(escapeText(piece.text));
  }
  for (const id of [...open].reverse()) {
    out.push(`</${id}>`);
  }
  return { tagged: out.join(''), tagMap };
}

function firstColour(pieces: Piece[]): string | null {
  for (const piece of pieces) {
    if (piece.colour !== null) {
      return piece.colour;
    }
  }
  return null;
}

function textNode(text: string, sem: SemMark[], colour: string | null): Record<string, unknown> {
  const marks: Record<string, unknown>[] = [];
  if (colour !== null) {
    marks.push({ type: 'textStyle', attrs: { color: colour } });
  }
  for (const mark of sem) {
    marks.push(
      mark.attrs === undefined ? { type: mark.type } : { type: mark.type, attrs: mark.attrs },
    );
  }
  return marks.length > 0 ? { type: 'text', text, marks } : { type: 'text', text };
}

// Split a run of consecutive source text nodes into sentences. When a sentence
// mixes colours (rare) each coloured span is tagged so the colour survives
// reassembly; when it is a single colour, colour stays presentational (untagged)
// and is reapplied on reassembly, so the common case sends no colour tags.
function runSentences(textNodes: Record<string, unknown>[]): Sentence[] {
  const pieces = piecesOf(textNodes);
  const full = pieces.map((piece) => piece.text).join('');
  const ranges = sentenceRanges(full);
  const sentences: Sentence[] = [];
  for (const [from, to] of ranges) {
    const slice = slicePieces(pieces, from, to);
    const multiColour = new Set(slice.map((piece) => piece.colour)).size > 1;
    const forTagging: Piece[] = slice.map((piece) =>
      multiColour && piece.colour !== null
        ? { ...piece, sem: [...piece.sem, { type: 'textStyle', attrs: { color: piece.colour } }] }
        : piece,
    );
    const { tagged, tagMap } = serialiseSentence(forTagging);
    const colour = multiColour ? null : firstColour(slice);
    const sourceNodes = slice.map((piece) => textNode(piece.text, piece.sem, piece.colour));
    sentences.push({ tagged, plain: full.slice(from, to), tagMap, colour, sourceNodes });
  }
  return sentences;
}

// A block's inline content, partitioned into maximal runs of text-node indices;
// the non-text inline nodes (hardBreak, inline images) that separate them are
// left out of the runs so reassembly can preserve them in place.
function partitionInline(content: unknown[]): number[][] {
  const runs: number[][] = [];
  let current: number[] = [];
  content.forEach((child, index) => {
    if (isRecord(child) && isTextNode(child)) {
      current.push(index);
    } else if (current.length > 0) {
      runs.push(current);
      current = [];
    }
  });
  if (current.length > 0) {
    runs.push(current);
  }
  return runs;
}

// -- Traversal (shared emit order for extract + reassembly) --------------------

// `sectionHeading` is display-only context (the nearest preceding page heading,
// headings emitted under their own heading); it is never part of the key and is
// undefined/null for conversations.
type Emit = (sentence: Sentence, locator: SegmentLocator, sectionHeading?: string) => void;

function collectText(node: unknown): string {
  if (Array.isArray(node)) {
    return node.map(collectText).join('');
  }
  if (isRecord(node)) {
    if (isTextNode(node)) {
      return node.text;
    }
    return Object.values(node).map(collectText).join('');
  }
  return '';
}

function makeOccurrence(): (plain: string) => number {
  const counts = new Map<string, number>();
  return (plain) => {
    const n = counts.get(plain) ?? 0;
    counts.set(plain, n + 1);
    return n;
  };
}

function isLeafTextBlock(node: Record<string, unknown>): boolean {
  const content = node['content'];
  return Array.isArray(content) && content.some((child) => isRecord(child) && isTextNode(child));
}

function walkPageBlocks(
  node: unknown,
  emit: (sentence: Sentence, sectionHeading: string | undefined) => void,
  state: { sectionHeading: string | undefined } = { sectionHeading: undefined },
): void {
  if (Array.isArray(node)) {
    node.forEach((child) => walkPageBlocks(child, emit, state));
    return;
  }
  if (!isRecord(node)) {
    return;
  }
  if (node['type'] === 'heading') {
    const headingText = collectText(node).trim();
    if (headingText !== '') {
      state.sectionHeading = headingText;
    }
  }
  const content = node['content'];
  if (Array.isArray(content) && isLeafTextBlock(node)) {
    for (const run of partitionInline(content)) {
      const nodes = run.map((index) => content[index]).filter(isRecord);
      for (const sentence of runSentences(nodes)) {
        emit(sentence, state.sectionHeading);
      }
    }
    return;
  }
  if (Array.isArray(content)) {
    content.forEach((child) => walkPageBlocks(child, emit, state));
  } else {
    for (const value of Object.values(node)) {
      walkPageBlocks(value, emit, state);
    }
  }
}

// The segment key hashes the TRIMMED sentence: the segmenter attaches the
// inter-sentence space to the preceding sentence, so appending a sentence makes a
// former-last sentence gain a trailing space. Trimming before hashing keeps that
// untouched neighbour's key stable (no needless re-translation / orphaned
// override). Reassembly still uses the untrimmed `plain` via restoreEdgeWhitespace,
// so displayed spacing is unchanged. Occurrence-counting also uses the trimmed
// text, so two sentences differing only by edge space ("Repeat. " vs "Repeat.")
// stay distinct slots via occurrenceIndex.
function keyText(plain: string): string {
  return plain.trim();
}

function walkConversationNode(node: unknown, emit: Emit): void {
  if (!isRecord(node) || typeof node['id'] !== 'string') {
    return;
  }
  const nodeId = node['id'];
  const data = node['data'];
  if (!isRecord(data)) {
    return;
  }
  const occ = makeOccurrence();
  if (data['tiptap'] !== undefined) {
    walkPageBlocks(data['tiptap'], (sentence) => {
      const key = keyText(sentence.plain);
      emit(sentence, {
        kind: 'CONVERSATION_SENTENCE',
        nodeId,
        sourceHash: hashSourceText(key),
        occurrenceIndex: occ(key),
      });
    });
  }
  const options = data['options'];
  if (Array.isArray(options)) {
    options.forEach((option, optionIndex) => {
      if (isRecord(option) && typeof option['title'] === 'string') {
        const title = option['title'];
        emit(
          { tagged: title, plain: title, tagMap: new Map(), colour: null, sourceNodes: [] },
          { kind: 'CONVERSATION_OPTION', nodeId, optionIndex },
        );
      }
    });
  }
}

function walkSegments(content: unknown, emit: Emit): void {
  if (isConversation(content)) {
    for (const node of content.nodes) {
      walkConversationNode(node, emit);
    }
    return;
  }
  const occ = makeOccurrence();
  walkPageBlocks(content, (sentence, sectionHeading) => {
    const key = keyText(sentence.plain);
    emit(
      sentence,
      {
        kind: 'PAGE_SENTENCE',
        sourceHash: hashSourceText(key),
        occurrenceIndex: occ(key),
      },
      sectionHeading,
    );
  });
}

// `sectionHeadings[i]` pairs with `segments[i]`/`locators[i]`: the nearest
// preceding page heading, or null (all null for conversations).
export function extractTextNodes(content: unknown): {
  segments: string[];
  locators: SegmentLocator[];
  sectionHeadings: (string | null)[];
} {
  const segments: string[] = [];
  const locators: SegmentLocator[] = [];
  const sectionHeadings: (string | null)[] = [];
  walkSegments(content, (sentence, locator, sectionHeading) => {
    segments.push(sentence.tagged);
    locators.push(locator);
    sectionHeadings.push(sectionHeading ?? null);
  });
  return { segments, locators, sectionHeadings };
}

// -- Reassembly ----------------------------------------------------------------

const TAG_RE = /<(\/?)(t\d+)>/g;

// Parse a translated tagged sentence into text nodes, resolving each tag id to
// its source mark (link href, etc.) and stamping the block colour. Throws on
// malformed or unknown tags so the caller can fall back to the English source.
function parseTagged(
  tagged: string,
  tagMap: Map<string, SemMark>,
  colour: string | null,
): Record<string, unknown>[] {
  const nodes: Record<string, unknown>[] = [];
  const open: string[] = [];
  let cursor = 0;
  const pushText = (raw: string): void => {
    if (raw === '') {
      return;
    }
    const sem = open.map((id) => {
      const mark = tagMap.get(id);
      if (mark === undefined) {
        throw new Error(`unknown tag ${id}`);
      }
      return mark;
    });
    nodes.push(textNode(unescapeText(raw), sem, colour));
  };
  TAG_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = TAG_RE.exec(tagged)) !== null) {
    pushText(tagged.slice(cursor, match.index));
    cursor = match.index + match[0].length;
    const slash = match[1];
    const id = match[2]!;
    if (slash === '/') {
      if (open.pop() !== id) {
        throw new Error(`mismatched close ${id}`);
      }
    } else {
      if (!tagMap.has(id)) {
        throw new Error(`unknown tag ${id}`);
      }
      open.push(id);
    }
  }
  pushText(tagged.slice(cursor));
  if (open.length > 0) {
    throw new Error('unclosed tag');
  }
  return nodes;
}

// Force the sentence's leading/trailing whitespace to match the English source.
// Segmenter puts inter-sentence spaces on the preceding sentence, but the model
// routinely trims them, so concatenating translated sentences would glue them
// (`calcoli.Se`). Only the edges are touched; internal spacing is the model's.
function restoreEdgeWhitespace(
  nodes: Record<string, unknown>[],
  sourcePlain: string,
): Record<string, unknown>[] {
  if (nodes.length === 0) {
    return nodes;
  }
  const lead = /^\s*/.exec(sourcePlain)?.[0] ?? '';
  const trail = /\s*$/.exec(sourcePlain)?.[0] ?? '';
  const hasText = (node: Record<string, unknown>): boolean => typeof node['text'] === 'string';

  // lead/trail are measured across the whole sentence, so they can span more than
  // one node: "<b>choice. </b>" followed by a bare " " puts one of the two trailing
  // spaces inside the bold node. Stripping only the outermost node left that one in
  // place and appending the full run then counted it twice, so a round trip grew by
  // a character. Strip across every node the whitespace covers instead.
  const stripEdge = (order: number[], pattern: RegExp): void => {
    for (const index of order) {
      const node = nodes[index]!;
      if (!hasText(node)) {
        return;
      }
      const kept = (node['text'] as string).replace(pattern, '');
      node['text'] = kept;
      if (kept !== '') {
        return;
      }
    }
  };

  const forwards = nodes.map((_, index) => index);
  stripEdge([...forwards].reverse(), /\s+$/);
  stripEdge(forwards, /^\s+/);

  // Nodes emptied by that strip are dropped: parseTagged never emits an empty text
  // node, so an empty one here is always our own doing, and ProseMirror rejects it.
  const remaining = nodes.filter((node) => !hasText(node) || node['text'] !== '');
  const kept = remaining.length > 0 ? remaining : [nodes[0]!];
  const first = kept[0]!;
  const last = kept[kept.length - 1]!;
  if (hasText(first)) {
    first['text'] = lead + (first['text'] as string);
  }
  if (hasText(last)) {
    // For an all-whitespace sentence lead and trail each match the entire string,
    // and there is no core between them to keep, so it is restored verbatim.
    last['text'] = sourcePlain.trim() === '' ? sourcePlain : (last['text'] as string) + trail;
  }
  return kept;
}

// One sentence slot's replacement. `value` empty/broken falls back to English;
// `markEdited` stamps the served nodes with the edited-since-publish mark.
type Replacement = { value: string; markEdited?: boolean };
type Next = (source: string) => Replacement;

function withEditedMark(nodes: Record<string, unknown>[]): Record<string, unknown>[] {
  return nodes.map((node) => {
    if (!isTextNode(node)) {
      return node;
    }
    const marks = Array.isArray(node['marks']) ? node['marks'] : [];
    return { ...node, marks: [...marks, { type: EDITED_SINCE_PUBLISH_MARK }] };
  });
}

// Rebuild a run's inline content from its translated sentences. `next` yields
// each sentence's replacement in emit order; a broken or empty value falls back
// to the English source. A `markEdited` slot is stamped whichever way it resolves.
function rebuildRun(sourceNodes: Record<string, unknown>[], next: Next): unknown[] {
  const out: unknown[] = [];
  for (const sentence of runSentences(sourceNodes)) {
    const { value, markEdited } = next(sentence.tagged);
    const stamp = (nodes: Record<string, unknown>[]): Record<string, unknown>[] =>
      markEdited === true ? withEditedMark(nodes) : nodes;
    if (typeof value !== 'string' || value === '') {
      out.push(...stamp(sentence.sourceNodes));
      continue;
    }
    try {
      const parsed = parseTagged(value, sentence.tagMap, sentence.colour);
      out.push(...stamp(restoreEdgeWhitespace(parsed, sentence.plain)));
    } catch {
      out.push(...stamp(sentence.sourceNodes));
    }
  }
  return out;
}

function rebuildLeafBlockContent(content: unknown[], next: Next): unknown[] {
  const runs = partitionInline(content);
  const runByStart = new Map<number, number[]>();
  const inRun = new Set<number>();
  for (const run of runs) {
    runByStart.set(run[0]!, run);
    for (const index of run) {
      inRun.add(index);
    }
  }
  const out: unknown[] = [];
  content.forEach((child, index) => {
    const run = runByStart.get(index);
    if (run !== undefined) {
      const nodes = run.map((i) => content[i]).filter(isRecord);
      out.push(...rebuildRun(nodes, next));
      return;
    }
    if (!inRun.has(index)) {
      out.push(child);
    }
  });
  return out;
}

// MUST mirror `walkPageBlocks` exactly, or extract and reassembly emit/consume a
// different number of slots and values land in the wrong place. Leaf text block:
// rebuild its inline content. Non-leaf with a content array: descend ONLY
// `content` (like the extract walk), preserving other keys untouched. No content
// array: descend every value.
function rebuildPage(node: unknown, next: Next): unknown {
  if (Array.isArray(node)) {
    return node.map((child) => rebuildPage(child, next));
  }
  if (!isRecord(node)) {
    return node;
  }
  const content = node['content'];
  if (Array.isArray(content)) {
    if (isLeafTextBlock(node)) {
      return { ...node, content: rebuildLeafBlockContent(content, next) };
    }
    return { ...node, content: content.map((child) => rebuildPage(child, next)) };
  }
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(node)) {
    out[key] = rebuildPage(value, next);
  }
  return out;
}

function rebuildOptions(options: unknown, next: Next): unknown {
  if (!Array.isArray(options)) {
    return options;
  }
  return options.map((option) => {
    if (isRecord(option) && typeof option['title'] === 'string') {
      const { value } = next(option['title']);
      const title = typeof value === 'string' && value !== '' ? value : option['title'];
      return { ...option, title };
    }
    return option;
  });
}

function rebuildConversationNode(node: unknown, next: Next): unknown {
  if (!isRecord(node) || typeof node['id'] !== 'string' || !isRecord(node['data'])) {
    return node;
  }
  const data = node['data'];
  const nextData: Record<string, unknown> = { ...data };
  if (data['tiptap'] !== undefined) {
    nextData['tiptap'] = rebuildPage(data['tiptap'], next);
  }
  if (Array.isArray(data['options'])) {
    nextData['options'] = rebuildOptions(data['options'], next);
  }
  return { ...node, data: nextData };
}

// Rewrites every translatable sentence in place; the walk order MUST mirror
// `extractTextNodes` emit order so positional values land in the right slots.
// `fn` receives the source (tagged) sentence and returns the replacement.
// Generic so composed content keeps its input type (student CDN read + admin
// preview rely on this).
export function mapTranslatableStrings<TContent>(
  content: TContent,
  fn: (source: string) => string,
): TContent {
  return rebuildContent(content, (source) => ({ value: fn(source) }));
}

// Shared reassembly core: walks the content once, calling `next` per sentence
// slot in emit order. `mapTranslatableStrings` and `applyTextNodesByKey` both
// build on this; the latter uses the richer `Replacement` to mark edited slots.
function rebuildContent<TContent>(content: TContent, next: Next): TContent {
  const rebuilt = isConversation(content)
    ? { ...content, nodes: content.nodes.map((node) => rebuildConversationNode(node, next)) }
    : rebuildPage(content, next);
  // The walk only swaps string values, preserving shape, so the type holds.
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return rebuilt as TContent;
}

// Splices `translated[i]` into the i-th sentence slot. Guards positional
// alignment: the count must match, and when `sourceSegments` is supplied each
// slot's live source must still equal the source the translation was produced
// against, so content edited since extraction can't land a value in the wrong
// slot.
export function applyTextNodes(
  content: unknown,
  translated: string[],
  sourceSegments?: string[],
): unknown {
  const live = extractTextNodes(content).segments;
  if (translated.length !== live.length) {
    throw new Error(
      `Translation count mismatch: expected ${live.length} segments, received ${translated.length}`,
    );
  }
  if (sourceSegments !== undefined) {
    for (let i = 0; i < live.length; i += 1) {
      if (live[i] !== sourceSegments[i]) {
        throw new Error(`Segment ${i} drifted since extraction; refusing to splice.`);
      }
    }
  }
  let index = 0;
  return mapTranslatableStrings(content, () => {
    const value = translated[index];
    index += 1;
    return value ?? '';
  });
}

// Per-segment overlay for the partial preview: splice a served value into each
// live slot whose segment key is in `valuesByKey`, leave the rest in English and
// stamp them with the edited-since-publish mark. Matching is by content-hash key
// (not position), so an added, removed, or reordered sentence only flags itself.
// Unlike `applyTextNodes` it never throws on drift, which is the whole point.
export function applyTextNodesByKey<TContent>(
  content: TContent,
  valuesByKey: ReadonlyMap<string, string>,
): { content: TContent; translatedCount: number; editedCount: number } {
  const { locators } = extractTextNodes(content);
  let index = 0;
  let translatedCount = 0;
  let editedCount = 0;
  const rebuilt = rebuildContent(content, () => {
    const locator = locators[index];
    index += 1;
    const value = locator === undefined ? undefined : valuesByKey.get(toSegmentKey(locator));
    if (value === undefined || value === '') {
      editedCount += 1;
      return { value: '', markEdited: true };
    }
    translatedCount += 1;
    return { value };
  });
  return { content: rebuilt, translatedCount, editedCount };
}
